import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Select,
  Input,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { createMovementGroup } from '../../api/endpointsBackend';
import { formatMoney } from '../../utils/formatters';

const GROUP_TYPE_VALUES = ['purchase', 'refund', 'split', 'installment', 'related'];

export default function GroupMovementsModal({ isOpen, onClose, movements, onGroupComplete }) {
  const toast = useToast();
  const { t } = useTranslation('movements');
  const [groupType, setGroupType] = useState('related');
  const [groupName, setGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleClose = () => {
    setGroupType('related');
    setGroupName('');
    setIsCreating(false);
    onClose();
  };

  const handleCreateGroup = async () => {
    setIsCreating(true);
    try {
      const movementIds = movements.map(m => m.id);
      const result = await createMovementGroup({
        name: groupName.trim() || undefined,
        type: groupType,
        movement_ids: movementIds,
      });
      toast({
        title: t('modals.group.toasts.successTitle'),
        description: t('modals.group.toasts.successDescription', { count: movements.length, type: t(`modals.group.types.${groupType}`) }),
        status: 'success',
        duration: 3000,
      });
      handleClose();
      onGroupComplete?.(result.id, movementIds);
    } catch (error) {
      toast({
        title: t('modals.group.toasts.errorTitle'),
        description: error.response?.data?.detail || t('modals.group.toasts.errorDescription'),
        status: 'error',
        duration: 4000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={2}>
          {t('modals.group.title')}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text fontSize="sm" color="redesign.text" mb={4}>
            {t('modals.group.description', { count: movements.length })}
          </Text>

          <FormControl mb={3}>
            <FormLabel fontSize="sm" fontWeight="600">{t('modals.group.typeLabel')}</FormLabel>
            <Select
              size="sm"
              value={groupType}
              onChange={(e) => setGroupType(e.target.value)}
            >
              {GROUP_TYPE_VALUES.map((value) => (
                <option key={value} value={value}>{t(`modals.group.types.${value}`)}</option>
              ))}
            </Select>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="sm" fontWeight="600">{t('modals.group.nameLabel')}</FormLabel>
            <Input
              size="sm"
              placeholder={t('modals.group.namePlaceholder')}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </FormControl>

          <Text fontSize="sm" fontWeight="600" mb={2}>
            {t('modals.group.movementsLabel')}
          </Text>
          <Box maxH="250px" overflowY="auto">
            {movements.map((mov) => (
              <Flex
                key={mov.id}
                py={2}
                px={3}
                bg="redesign.bg.card"
                align="center"
                mb="1px"
                borderRadius="sm"
                borderLeft="3px solid"
                borderLeftColor="redesign.accent.blue"
              >
                <Box flex={1} minW={0}>
                  <Text fontSize="xs" fontWeight="600" noOfLines={1}>
                    {mov.note || mov.description || t('fallback.noNote')}
                  </Text>
                  <Text fontSize="2xs" color="redesign.text.muted">
                    {mov.account?.name || t('fallback.unknown')} &middot; {mov.date}
                  </Text>
                </Box>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  ml={2}
                  flexShrink={0}
                  color={
                    mov.type === 'Income' ? 'redesign.amount.income' :
                    mov.type === 'Expenses' ? 'redesign.amount.expense' :
                    'redesign.amount.neutral'
                  }
                >
                  {formatMoney(mov.amount, mov.currency)}
                </Text>
              </Flex>
            ))}
          </Box>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={handleClose}>
            {t('modals.group.buttons.cancel')}
          </Button>
          <Button
            size="sm"
            bg="redesign.heading"
            color="redesign.bg.surface"
            _hover={{ bg: 'redesign.text' }}
            onClick={handleCreateGroup}
            isLoading={isCreating}
          >
            {t('modals.group.buttons.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
